import React from "react";
import styled from "styled-components";
import { getLocale } from "react-i18nify";

import { Box, Grid, Stack, Typography } from "@mui/material";

import font from "src/styles/font";
import device from "src/styles/device";
import {
  checkNotEmpty,
  // convertIntToCurrency,
} from "src/common";
import { Black, DarkGrey, GreyStroke, White, White01 } from "src/styles/color";
import { hexToRgba } from "src/common/color";

import { UseUpload } from "src/context/UploadProductContext";

import LabelSection from "src/components/core/LabelSection";
import Chip from "src/components/core/Chip";

const SectionBox = styled.div`
  width: 100%;
  background: ${White};
  /* border: 1px solid ${GreyStroke}; */
  border-top: 12px solid ${({ fill }) => fill || GreyStroke};
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  padding: 16px 20px;

  @media ${device.mobileL} {
    padding: 12px 16px;
  }
`;

const CategoryTitle = styled.div`
  width: 100%;
  padding: 12px;
  border-radius: 12px;
  font-size: ${font.size.md};
  font-weight: 500;
  line-height: 24px;
  color: ${({ fill }) => fill || Black};
  background: ${({ fill }) => hexToRgba(fill, 0.2) || White01};
  margin-bottom: 16px;

  @media ${device.mobileL} {
    font-size: ${font.size.sm};
  }
`;

const CategorySubTitle = styled.div`
  font-size: ${font.size.sm};
  font-weight: 500;
  line-height: 20px;
  color: ${DarkGrey};
  margin-bottom: 16px;
  border-bottom: 1px solid ${GreyStroke};

  @media ${device.mobileL} {
    font-size: ${font.size.xs};
  }
`;

const WrapperColorIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid ${GreyStroke};
  background-color: ${({ color }) => color || White};
`;

const fillColors = ["#E13B30", "#219653", "#005FCC", "#C09020"];

const LoadingBox = styled(Box)`
  width: 100%;
  height: 100%;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PopularTrends = ({ data = [], isInTrend = null, isSeller = false }) => {
  const { masterdata } = UseUpload();
  const { color_data } = masterdata;

  if (!checkNotEmpty(color_data))
    return (
      <LoadingBox>
        <Typography
          variant="body2"
          sx={{ color: DarkGrey, fontSize: font.size.sm }}
        >
          {getLocale() === "th" ? `กำลังโหลดข้อมูล...` : `Loading data...`}
        </Typography>
      </LoadingBox>
    );

  return (
    <LabelSection
      title={getLocale() === "th" ? "เทรนสินค้า" : "Product Trends"}
      description={
        <Typography
          variant="body2"
          sx={{ ml: 2.5, "&.MuiTypography-root": { fontSize: font.size.xs } }}
        >
          {getLocale() === "th"
            ? isSeller
              ? `ตามหมวดหมู่สินค้าของร้าน`
              : `ตามหมวดหมู่สินค้าทั้งหมด`
            : isSeller
              ? `Based on your store's product categories`
              : `Based on all product categories`}
        </Typography>
      }
      style={{ marginBottom: 0, height: "100%" }}
      titleStyle={{ fontSize: font.size.lg, fontWeight: 600 }}
      headerStyle={{ marginBottom: 16 }}
    >
      <Grid container spacing={{ xs: 1.5, sm: 2.5, md: 2 }} sx={{ flex: 1 }}>
        {checkNotEmpty(data) ? (
          <>
            {data.map((item, index) => {
              let fill = fillColors[index % fillColors.length];
              return (
                <Grid item xs={12} sm={6} lg={3} key={index}>
                  <SectionBox fill={fill}>
                    <CategoryTitle fill={fill}>{item?.typeTh}</CategoryTitle>

                    <CategorySubTitle>
                      {getLocale() === "th" ? "สียอดนิยม" : "Popular Colors"}
                    </CategorySubTitle>
                    {item?.topColors?.map((topColor, colorIndex) => {
                      let findColor =
                        color_data.find((c) => c.color_en === topColor.name) ||
                        topColor;
                      let color = { ...findColor, views: topColor.views };
                      return (
                        <Stack
                          key={`color-${colorIndex}`}
                          direction="row"
                          // alignItems="center"
                          spacing={1}
                          sx={{ mb: 1.5 }}
                        >
                          {color?.icon_img ? (
                            <img
                              src={color?.icon_img}
                              alt={color?.color_en}
                              style={{
                                width: 20,
                                height: 20,
                                borderRadius: 4,
                                border: `1px solid ${GreyStroke}`,
                              }}
                            />
                          ) : (
                            <WrapperColorIcon></WrapperColorIcon>
                          )}
                          <Box>
                            <Typography
                              variant="body1"
                              sx={{
                                "&.MuiTypography-root": {
                                  lineHeight: 1.2,
                                },
                              }}
                            >
                              {color?.[`color_${getLocale()}`]}
                            </Typography>
                            {/* <Typography
                              variant="body2"
                              sx={{
                                "&.MuiTypography-root": {
                                  fontSize: font.size.xs,
                                  lineHeight: 1,
                                },
                              }}
                            >
                              {convertIntToCurrency(color.views, false)}{" "}
                              {getLocale() === "th" ? "ครั้ง" : "views"}
                            </Typography> */}
                          </Box>
                        </Stack>
                      );
                    })}

                    <CategorySubTitle>
                      {getLocale() === "th"
                        ? "ลวดลายยอดนิยม"
                        : "Popular Patterns"}
                    </CategorySubTitle>
                    {item?.topPatterns?.map((pattern, patternIndex) => {
                      return (
                        <Stack
                          key={`pattern-${patternIndex}`}
                          direction="row"
                          // alignItems="center"
                          spacing={1}
                          sx={{ mb: 1.5 }}
                        >
                          <Chip
                            label={patternIndex + 1}
                            size="small"
                            sx={{
                              minWidth: 24,
                              "&.MuiChip-root.MuiChip-filled.MuiChip-colorDefault":
                                { backgroundColor: fill },
                            }}
                          />
                          <Box sx={{ mb: 1.5 }}>
                            <Typography variant="body2">
                              {pattern?.name}
                            </Typography>
                            {/* <Typography
                              variant="body2"
                              sx={{
                                "&.MuiTypography-root": {
                                  fontSize: font.size.xs,
                                  lineHeight: 1,
                                },
                              }}
                            >
                              {convertIntToCurrency(pattern.views, false)}{" "}
                              {getLocale() === "th" ? "ครั้ง" : "views"}
                            </Typography> */}
                          </Box>
                        </Stack>
                      );
                    })}

                    <CategorySubTitle>
                      {getLocale() === "th"
                        ? "ผิวสัมผัสยอดนิยม"
                        : "Popular Textures"}
                    </CategorySubTitle>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      {item?.topTextures?.map((texture, textureIndex) => (
                        <Chip
                          key={`texture-${textureIndex}`}
                          label={texture?.name}
                          sx={{
                            "&.MuiChip-root.MuiChip-filled.MuiChip-colorDefault":
                              {
                                backgroundColor: hexToRgba(fill, 0.2),
                                color: fill,
                              },
                            "&.MuiChip-root.MuiChip-filled .MuiChip-label": {
                              color: fill,
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </SectionBox>
                </Grid>
              );
            })}
          </>
        ) : (
          <Grid item xs={12}>
            <Box
              sx={{
                width: "100%",
                height: "100%",
                minHeight: { xs: 200, md: 300 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: DarkGrey, fontSize: font.size.sm }}
              >
                {getLocale() === "th" ? `ไม่มีข้อมูล` : `No data available`}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </LabelSection>
  );
};

export default PopularTrends;
